import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Boot sequence animation for the preview screen.
 * Shows a terminal-style startup with QooQoo branding and Magic Forest mission.
 * Phase 1: Splash logo with progress bar
 * Phase 2: Scrolling colorful log lines
 */

const BOOT_LOGS = [
  { text: '> QOOQOO VISUAL ENGINE v1.0.0', color: '#e2e8f0', bold: true },
  { text: '> ─────────────────────────────────', color: '#334155' },
  { text: '> CORE INIT: LOADING MODULES...', color: '#9be945' },
  { text: '> ORG: MAGIC FOREST ASSOCIATION', color: '#9be945' },
  { text: '> TYPE: NONPROFIT / OPEN SOURCE', color: '#9be945' },
  { text: '> ─────────────────────────────────', color: '#334155' },
  { text: '> MISSION_BRIEF:', color: '#fbbf24', bold: true },
  { text: '>   RAISE FUNDS. BUY LAND.', color: '#fbbf24' },
  { text: '>   PLANT A REAL FOREST.', color: '#fbbf24' },
  { text: '> ─────────────────────────────────', color: '#334155' },
  { text: '> THIS SOFTWARE IS FREE. FOREVER.', color: '#e2e8f0', bold: true },
  { text: '> BUILT TO HELP YOU CREATE SOCIAL', color: '#9be945' },
  { text: '> MEDIA POSTS FASTER AND EASIER.', color: '#9be945' },
  { text: '> ─────────────────────────────────', color: '#334155' },
  { text: '> LOADING CAROUSEL BUILDER...', color: '#9be945' },
  { text: '> TEMPLATE ENGINE: ██████████ OK', color: '#34d399' },
  { text: '> FONT SUBSYSTEM: 14 FAMILIES', color: '#34d399' },
  { text: '> COLOR PIPELINE: INITIALIZED', color: '#34d399' },
  { text: '> ASSET STORAGE: S3 CONNECTED', color: '#34d399' },
  { text: '> EXPORT MODULE: WORKER_PRIMARY', color: '#34d399' },
  { text: '> RENDER ENGINE: PLAYWRIGHT OK', color: '#34d399' },
  { text: '> ─────────────────────────────────', color: '#334155' },
  { text: '> ALL SYSTEMS NOMINAL.', color: '#e2e8f0', bold: true },
  { text: '> CREATE. SHARE. GROW THE FOREST.', color: '#9be945', bold: true },
  { text: '> READY FOR INPUT._', color: '#9be945', bold: true, blink: true },
];

const SPLASH_DURATION = 2800;
const LOG_START_DELAY = 300;

export function BootSequence() {
  const [phase, setPhase] = useState('splash'); // 'splash' | 'logs'
  const [logLines, setLogLines] = useState([]);
  const [splashProgress, setSplashProgress] = useState(0);
  const logContainerRef = useRef(null);
  const logIndexRef = useRef(0);
  const timerRef = useRef(null);

  // Phase 1: Splash progress
  useEffect(() => {
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / SPLASH_DURATION) * 100);
      setSplashProgress(pct);
      if (elapsed < SPLASH_DURATION) {
        requestAnimationFrame(tick);
      }
    };
    requestAnimationFrame(tick);

    const t = setTimeout(() => setPhase('logs'), SPLASH_DURATION);
    return () => clearTimeout(t);
  }, []);

  // Phase 2: Log lines
  const addLog = useCallback((index) => {
    if (index >= BOOT_LOGS.length) return;
    setLogLines(prev => {
      const next = [...prev, BOOT_LOGS[index]];
      // Keep last ~16 lines visible
      if (next.length > 16) return next.slice(next.length - 16);
      return next;
    });
    const delay = BOOT_LOGS[index].text.includes('───') ? 60 : (Math.random() * 180 + 80);
    timerRef.current = setTimeout(() => addLog(index + 1), delay);
  }, []);

  useEffect(() => {
    if (phase !== 'logs') return;
    const t = setTimeout(() => addLog(0), LOG_START_DELAY);
    return () => {
      clearTimeout(t);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [phase, addLog]);

  // Auto-scroll log container
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logLines]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
        overflow: 'hidden',
        zIndex: 5,
      }}
    >
      {/* Scanline effect */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '80px',
          background: 'linear-gradient(0deg, transparent 0%, rgba(155,233,69,0.04) 50%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 20,
          animation: 'boot-scanline 5s linear infinite',
        }}
      />

      {/* Phase 1: Splash */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10,
          background: '#111',
          opacity: phase === 'splash' ? 1 : 0,
          transition: 'opacity 0.5s ease',
          pointerEvents: phase === 'splash' ? 'auto' : 'none',
        }}
      >
        <div
          style={{
            fontFamily: "'Shrikhand', cursive",
            fontSize: '56px',
            color: '#e2e8f0',
            letterSpacing: '-2px',
            opacity: splashProgress > 5 ? 1 : 0,
            transform: splashProgress > 5 ? 'scale(1)' : 'scale(0.85)',
            transition: 'all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            textShadow: '0 0 30px rgba(155,233,69,0.15)',
          }}
        >
          QooQoo
        </div>

        <div
          style={{
            marginTop: '8px',
            fontSize: '10px',
            fontWeight: 600,
            color: '#4d7a1e',
            letterSpacing: '3px',
            textTransform: 'uppercase',
            opacity: splashProgress > 15 ? 1 : 0,
            transition: 'opacity 0.6s ease',
          }}
        >
          Magic Forest Association
        </div>

        {/* Progress bar */}
        <div
          style={{
            width: '180px',
            height: '3px',
            background: '#1a1a1a',
            marginTop: '24px',
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${splashProgress}%`,
              background: 'linear-gradient(90deg, #4d7a1e, #9be945)',
              boxShadow: '0 0 8px rgba(155,233,69,0.4)',
              borderRadius: '2px',
              transition: 'width 0.1s linear',
            }}
          />
        </div>

        <div
          style={{
            marginTop: '10px',
            fontSize: '9px',
            color: '#555',
            letterSpacing: '1px',
          }}
        >
          SYSTEM INITIALIZING...
        </div>
      </div>

      {/* Phase 2: Boot logs */}
      {phase === 'logs' && (
        <div
          ref={logContainerRef}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '3px',
            padding: '16px 18px',
            overflow: 'hidden',
            justifyContent: 'flex-end',
          }}
        >
          {logLines.map((line, i) => (
            <div
              key={i}
              style={{
                color: line.color,
                fontSize: '10px',
                lineHeight: 1.5,
                fontWeight: line.bold ? 700 : 400,
                textShadow: line.color === '#9be945' || line.color === '#34d399'
                  ? `0 0 4px ${line.color}40`
                  : 'none',
                opacity: 0,
                animation: 'boot-line-appear 0.12s forwards',
                whiteSpace: 'pre',
              }}
            >
              {line.text}
            </div>
          ))}
        </div>
      )}

      {/* Bottom status bar */}
      <div
        style={{
          padding: '8px 18px',
          borderTop: '1px solid #1a2e10',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: '9px', fontWeight: 700, color: '#4d7a1e' }}>
          QOOQOO ENGINE v1.0.0
        </span>
        <span style={{ fontSize: '9px', color: '#334155' }}>
          {phase === 'splash' ? 'BOOTING...' : logLines.length >= BOOT_LOGS.length ? 'ONLINE' : 'LOADING...'}
        </span>
      </div>

      {/* Keyframe styles */}
      <style>{`
        @keyframes boot-scanline {
          0% { top: -80px; }
          100% { top: 100%; }
        }
        @keyframes boot-line-appear {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
